/**
 * `monetization/purchase` — **the client-side PLATFORM purchase clamp.**
 *
 * An EXACT mirror of `packages/core-rust/src/monetization/purchase.rs`. Both
 * halves must say the same thing, which is why the clamp does not live in the screen:
 * it lives here, in the kit, where it can be tested without a DOM and copied
 * and where a clone copies it whole.
 *
 * ## What fails without this
 *
 * Three admin-panel rows (`monetization.enabled`, `payments.enabled`,
 * `payments.provider=stripe`) can make an **already-installed APK** display «Upgrade»
 * and run `location.assign(checkoutUrl)` toward Stripe. Selling digital
 * content inside a binary distributed through Google Play without Play Billing is
 * a **removal reason**, and the switch reaches the binary **without
 * redeploying and without re-uploading**.
 *
 * ## Why BOTH layers are needed
 *
 * The server no longer returns a checkout URL to a store binary
 * (`billing_checkout_handler`). Even so, the client decides again for two
 * nonredundant reasons:
 *
 *   1. **The button**. An «Upgrade» that always fails is a broken screen; worse,
 *      it is a purchase surface visible inside the binary, which is
 *      exactly what the Play policy examines.
 *   2. **An already-installed binary does not update when the server changes.**
 *      An old APK talking to a new server is protected by the server;
 *      a new APK talking to an old server—or one from a different
 *      installation—protects itself. Only with both is the published
 *      binary safe regardless of what happens on the other side.
 *
 * ## What this is NOT
 *
 * This is not the Play billing layer: there is no `play` provider, native purchase,
 * or webhooks. That is a separate decision (`rescue/play-billing-950a8e5b` branch).
 * Here we only prevent selling where selling is illegal.
 /** Waits for the app to finish booting (skeleton overlay disappears). */

import { getPlatformOS, type PlatformOS } from "@fleet/utils";

/** Purchase surfaces distinguished by the portfolio. */
export const PURCHASE_PLATFORMS = ["web", "desktop", "android", "ios"] as const;

export type PurchasePlatform = (typeof PURCHASE_PLATFORMS)[number];

/** Unclassifiable surface ⇒ fail-closed. */
export const UNKNOWN_PURCHASE_PLATFORM = "unknown";

export type ResolvedPurchasePlatform = PurchasePlatform | typeof UNKNOWN_PURCHASE_PLATFORM;

/** “No gateway” provider: a human applies the upgrade. Always legal. */
export const PAYMENT_PROVIDER_NONE = "none";

/** Provider IDs this kit knows how to build (mirror of the Rust version). */
export const KNOWN_PAYMENT_PROVIDERS: readonly string[] = ["none", "stripe"];

export function isPurchasePlatform(value: string): value is PurchasePlatform {
  return (PURCHASE_PLATFORMS as readonly string[]).includes(value);
}

/**
 * **NON-NEGOTIABLE.** Which providers are LEGAL on each surface.
 *
 * - `web` / `desktop` — Stripe is legitimate: neither has the
 *   Play/App Store obligation (nor Microsoft Store).
 * - `android` — either Play Billing or nothing, and the Play provider does not exist in this
 *   exist in this kit ⇒ only `none` today.
 * - `ios` — only `none`: no Apple IAP provider.
 *
 * No configuration path can add an entry to this table: it is not
 * read from the configuration; it is read from here.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export const PLATFORM_ALLOWED_PAYMENT_PROVIDERS: Readonly<
  Record<PurchasePlatform, readonly string[]>
> = {
  web: [PAYMENT_PROVIDER_NONE, "stripe"],
  desktop: [PAYMENT_PROVIDER_NONE, "stripe"],
  android: [PAYMENT_PROVIDER_NONE],
  ios: [PAYMENT_PROVIDER_NONE],
};

/** Floor for an unclassified surface: only the manual provider. */
export const UNKNOWN_PLATFORM_ALLOWED_PROVIDERS: readonly string[] = [PAYMENT_PROVIDER_NONE];

/**
 * Surfaces that are **store binaries**: where the payments policy applies.
 * `desktop` is NOT one: it is a binary, but no store reviews it.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export const STORE_BINARY_PLATFORMS: readonly string[] = ["android", "ios"];

export function isStoreBinaryPlatform(platform: string): boolean {
  return STORE_BINARY_PLATFORMS.includes(platform);
}

/** Normalizes a declared surface. Total: anything unclassified ⇒ `unknown`. */
export function normalizePlatform(raw: string): ResolvedPurchasePlatform {
  const v = raw.trim().toLowerCase();
  return isPurchasePlatform(v) ? v : UNKNOWN_PURCHASE_PLATFORM;
}

/** Providers legal on a surface (fail-closed for unknown surfaces). */
export function allowedPaymentProviders(platform: string): readonly string[] {
  return isPurchasePlatform(platform)
    ? PLATFORM_ALLOWED_PAYMENT_PROVIDERS[platform]
    : UNKNOWN_PLATFORM_ALLOWED_PROVIDERS;
}

/**
 * **THE clamp.** EFFECTIVE provider on a surface: the configured one if legal
 * there, and `none` otherwise. Monotonic (only restricts), idempotent, and total.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export function clampProviderToPlatform(platform: string, configuredProvider: string): string {
  const provider = configuredProvider.trim().toLowerCase();
  return allowedPaymentProviders(platform).includes(provider) ? provider : PAYMENT_PROVIDER_NONE;
}

/**
 * Purchase-surface detection.
 *
 * The nuance most often handled incorrectly: **Chrome on an Android phone is NOT the Android
 * binary.** The Play policy applies to the APK distributed through Play, not to the
 * mobile web. That is why the first check is whether we are inside a native
 * runtime; only then does the operating system determine the surface.
 *
 * Fail-closed: a binary on an OS we cannot classify is `unknown`.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export function detectPurchasePlatform(input: {
  /** Are we running inside the binary (Tauri) or in a browser? */
  isNativeRuntime: boolean;
  os: PlatformOS;
}): ResolvedPurchasePlatform {
  if (!input.isNativeRuntime) {
    return "web";
  }
  switch (input.os) {
    case "android": {
      return "android";
    }
    case "ios": {
      return "ios";
    }
    case "macos":
    case "windows":
    case "linux": {
      return "desktop";
    }
    default: {
      return UNKNOWN_PURCHASE_PLATFORM;
    }
  }
}

/** Runtime convenience: the native flag is injected because it lives in the app. */
export function currentPurchasePlatformFor(isNativeRuntime: boolean): ResolvedPurchasePlatform {
  return detectPurchasePlatform({ isNativeRuntime, os: getPlatformOS() });
}

/** Why the surface ended up as it did (mirror of the Rust enum). */
export type PurchaseGateReason =
  | "offered"
  | "monetization-disabled"
  | "payments-disabled"
  | "unknown-platform"
  | "provider-not-allowed";

export interface PurchaseSurface {
  platform: ResolvedPurchasePlatform;
  /** EFFECTIVE provider after the non-negotiable clamp. */
  provider: string;
  offersPurchase: boolean;
  reason: PurchaseGateReason;
}

/** Purchase configuration seen by the resolver (subset of `MonetizationConfig`). */
export interface PurchaseSurfaceRequest {
  /**
   * Surface. It is a `string` by design: it may come from detection, a
   * configuration, or a server response, and the resolver must be TOTAL.
   /** Waits for the app to finish booting (skeleton overlay disappears). */
  platform: string;
  enabled: boolean;
  paymentsEnabled: boolean;
  provider: string;
}

/**
 * Composes configuration + surface and returns the effective purchase.
 *
 * Invariant supporting everything else: **the returned `provider` is always in
 * [`allowedPaymentProviders`] for the returned platform**, whether a purchase is offered
 * offered or not.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export function resolvePurchaseSurface(request: PurchaseSurfaceRequest): PurchaseSurface {
  const platform = normalizePlatform(request.platform);
  const configured = request.provider.trim().toLowerCase();
  const provider = clampProviderToPlatform(platform, configured);

  const gated = (reason: PurchaseGateReason): PurchaseSurface => ({
    platform,
    provider,
    offersPurchase: false,
    reason,
  });

  if (!request.enabled) {
    return gated("monetization-disabled");
  }
  if (!request.paymentsEnabled) {
    return gated("payments-disabled");
  }
  if (platform === UNKNOWN_PURCHASE_PLATFORM) {
    return gated("unknown-platform");
  }
  if (provider !== configured) {
    return gated("provider-not-allowed");
  }
  return { platform, provider, offersPurchase: true, reason: "offered" };
}
